import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  TextField,
  Box,
  Chip,
  Typography,
  alpha,
  InputAdornment,
} from "@mui/material";
import { Save, X } from "lucide-react";
import { generatePassword, copyPasswordToClipboard } from "../../../utils/passwordUtils";
import { useCreateUserMutation, UserAdminFieldsFragment } from "../../../graphql/graphql";
import { ROLES, roleColors } from "./constants";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";

const schema = z.object({
  name: z.string().min(1, "名前は必須です"),
  email: z.string().email("有効なメールアドレスを入力してください"),
  password: z.string().min(8, "パスワードは8文字以上必要です"),
  roleIds: z.array(z.string()).min(1, "ロールは1つ以上選択してください"),
});

type FormValues = z.infer<typeof schema>;

interface UserCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onComplete: (user: UserAdminFieldsFragment) => void;
}

export const UserCreateDialog = ({ open, onClose, onComplete }: UserCreateDialogProps) => {
  const { mutateAsync: createUser } = useCreateUserMutation();
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      roleIds: [],
    },
  });

  const onSubmit = async (data: FormValues) => {
    try {
      const result = await createUser(data);
      if (result?.createUser) {
        reset();
        onComplete(result.createUser);
      }
    } catch (error) {
      console.error("Failed to create user:", error);
      toast.error("ユーザーの作成に失敗しました");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>ユーザー追加</DialogTitle>
        <DialogContent>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="名前"
                margin="normal"
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="メールアドレス"
                margin="normal"
                error={!!errors.email}
                helperText={errors.email?.message}
              />
            )}
          />
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="パスワード"
                type="password"
                margin="normal"
                error={!!errors.password}
                helperText={errors.password?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        onClick={() => {
                          const password = generatePassword();
                          setValue('password', password, { shouldValidate: true });
                          
                          // クリップボードにコピー
                          copyPasswordToClipboard(password)
                            .then(success => {
                              if (success) {
                                toast.success('パスワードをクリップボードにコピーしました');
                              }
                            });
                        }}
                        size="small"
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        自動生成
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
          <FormControl fullWidth sx={{ mt: 2 }} error={!!errors.roleIds}>
            <Typography
              component="label"
              sx={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'text.primary',
                mb: 1,
              }}
            >
              ロール
            </Typography>
            <Controller
              name="roleIds"
              control={control}
              render={({ field }) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {ROLES.map((role) => {
                    const isSelected = field.value.includes(role.id);
                    const colors = roleColors[role.id];
                    return (
                      <Chip
                        key={role.id}
                        label={role.label}
                        onClick={() => {
                          if (isSelected) {
                            field.onChange(field.value.filter(id => id !== role.id));
                          } else {
                            field.onChange([...field.value, role.id]);
                          }
                        }}
                        sx={{
                          color: isSelected ? colors.color : 'text.secondary',
                          backgroundColor: isSelected ? colors.backgroundColor : 'transparent',
                          border: '1px solid',
                          borderColor: isSelected ? colors.color : 'divider',
                          fontWeight: isSelected ? 600 : 400,
                          '&:hover': {
                            backgroundColor: isSelected 
                              ? alpha(colors.backgroundColor, 0.8)
                              : alpha(colors.backgroundColor, 0.1),
                          },
                        }}
                      />
                    );
                  })}
                </Box>
              )}
            />
            {errors.roleIds && (
              <FormHelperText error>{errors.roleIds.message}</FormHelperText>
            )}
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} startIcon={<X size={20} />}>
            キャンセル
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<Save size={20} />}
            disabled={isSubmitting}
          >
            追加
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
